
    import {
      RendererUiPlugin,
      PopmotionPlugin,
      VelocityBufferPlugin,
      SimpleBackgroundEnvUiPlugin,
      LoadingScreenPlugin,
      TonemapPlugin,
      AssetManagerPlugin,
    } from "https://dist.pixotronics.com/webgi/runtime/bundle-0.17.0.mjs";

    import { initWatchVariantsSwiper } from "./src/swiper.js";

    import {
      cameraActivated,
      initTracking,
      interactionWithModel,
      modelLoaded,
      tapOnAr,
    } from "./src/tracking.js";

    const INSERTION = (window.__INSERTION__ || "fm").toLowerCase();

    const INSERTION_VARIANT = {
      fm: "Master (Free Media)",
      pca: "Paid Custom Audience",
      prtg: "Paid RTG",
    };

    async function loadConfig(ins) {
      const res = await fetch(`/insertions/${ins}.json`);
      if (!res.ok) {
        console.warn(`[i18n] Missing ${ins}.json, falling back to fm`);
        return fetch(`/insertions/fm.json`).then((r) => r.json());
      }
      return res.json();
    }

    var viewer = null;
    var loader = null;
    var showLoader = null;
    var hideLoader = null;
    var currentModel = "gold";
    var assetManager = null;

    var deepAR = null;
    var isARMode = false;
    var currentARModel = "gold";

    const watchVariants = {
      0: {
        name: "silver",
        model: "/assets/models/IWC_SS_3D.glb",
        deepar: "/assets/models/deepar/silver_10.deepar",
        background: "/assets/black_bg.webp",
      },
      1: {
        name: "gold",
        model: "/assets/models/IWC_Gold_3D.glb",
        deepar: "/assets/models/deepar/gold_12.deepar",
        background: "/assets/gold_bg.webp",
      },
      2: {
        name: "black",
        model: "/assets/models/IWC_Black_3D.glb",
        deepar: "/assets/models/deepar/black_06-3.deepar",
        background: "/assets/black_bg.webp",
      },
    };

    var hint = null;
    var hintTimer = null;
    var userTappedOnce = false;
    const HINT_DELAY = 3000;

    document.addEventListener("DOMContentLoaded", async (event) => {
      const cfg = await loadConfig(INSERTION);
      console.log("CONFIG LOADED:", cfg);
      initTracking(cfg);

      loader = document.querySelector(".dot-pulse-container");
      showLoader = () => (loader.style.display = "flex");
      hideLoader = () => (loader.style.display = "none");

      hint = document.getElementById("hint-cursor");

      showLoader();

      const discoverBtn = document.querySelector(".discover-btn");
      if (discoverBtn) {
        discoverBtn.addEventListener("mouseenter", () => {
          discoverBtn.style.transform = "scale(1.05)";
        });
        discoverBtn.addEventListener("mouseleave", () => {
          discoverBtn.style.transform = "scale(1)";
        });
      }

      const themeToggle = document.getElementById("toggle");
      if (themeToggle) {
        themeToggle.addEventListener("change", async (e) => {
          tapOnAr();
          const shouldBeARMode = e.target.checked;
          await switchMode(shouldBeARMode);
        });
      }

      setupViewer();
    });

    async function loadDeepARScript() {
      if (window.deepar) return;

      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        let attemptCount = 0;

        const tryLoad = (scriptSrc, description) => {
          attemptCount++;

          script.src = scriptSrc;
          script.onload = () => {
            resolve();
          };

          script.onerror = () => {
            if (scriptSrc.includes("./deepar-resources/")) {
              tryLoad(
                "https://cdn.jsdelivr.net/npm/deepar@5.6.3/dist/deepar.js",
                "CDN fallback"
              );
            } else {
              reject(
                new Error("DeepAR script failed to load from all sources")
              );
            }
          };
        };

        tryLoad("./deepar-resources/js/deepar.js", "Local resources");

        document.head.appendChild(script);
      });
    }

    async function initializeDeepAR() {
      if (deepAR) return deepAR;

      const startTime = performance.now();

      await loadDeepARScript();

      const previewElement = document.getElementById("deepar-container");

      if (!previewElement) {
        return;
      }

      previewElement.style.display = "block";

      const currentVariant = Object.values(watchVariants).find(
        (v) => v.name === currentModel
      );
      const currentEffect = currentVariant
        ? currentVariant.deepar
        : watchVariants[1].deepar;

      try {
        const DEEPAR_KEY =
          "f8bbfa81de066b2ce790665318eef1cdbd4c98eaad5e5d878c439b6c50d99a5fc9d91d5fde2d73eb";

        const testEffect = null;

        const isMobile =
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
          );

        let config = {
          licenseKey: DEEPAR_KEY,
          previewElement: previewElement,
          effect: testEffect,
          rootPath: "./deepar-resources/",
          additionalOptions: {
            cameraConfig: {
              facingMode: "environment",
            },
            canvasWidth: 353,
            canvasHeight: 622,
          },
        };

        deepAR = await window.deepar.initialize(config);

        setTimeout(async () => {
          if (currentEffect && currentEffect !== testEffect) {
            try {
              await deepAR.switchEffect(currentEffect);
              const effectVariant = Object.values(watchVariants).find(
                (v) => v.deepar === currentEffect
              );
              if (effectVariant) {
                currentARModel = effectVariant.name;
              }
            } catch (error) {
              console.error("Failed to load IWC effect:", error);
            }
          }
        }, 1000);

        deepAR.callbacks.onWristTracked = (wrist) => {
          if (hideLoader) {
            hideLoader();
          }
        };

        deepAR.callbacks.onEffectLoaded = () => {};

        deepAR.callbacks.onFaceTracked = (face) => {};

        deepAR.callbacks.onEffectLoaded = () => {};

        deepAR.callbacks.onFaceVisiblityChanged = (visible) => {};

        currentARModel = currentModel;

        return deepAR;
      } catch (error) {
        throw error;
      }
    }

    async function switchMode(toARMode) {
      const canvas3D = document.getElementById("main-canvas");
      const containerAR = document.getElementById("deepar-container");
      const backgroundImg = document.getElementById("background");
      const hintCursor = document.getElementById("hint-cursor");

      if (toARMode && !isARMode) {
        showLoader();
        try {
          await initializeDeepAR();

          if (currentModel !== currentARModel) {
            const currentVariant = Object.values(watchVariants).find(
              (v) => v.name === currentModel
            );
            if (currentVariant && deepAR) {
              try {
                await deepAR.switchEffect(currentVariant.deepar);
                currentARModel = currentModel;
                modelLoaded(currentARModel);
              } catch (error) {
                console.error("Failed to sync AR effect:", error);
              }
            }
          }

          canvas3D.style.display = "none";
          backgroundImg.style.display = "none";
          if (hintCursor) {
            hintCursor.style.display = "none";
            hintCursor.style.visibility = "hidden";
          }

          containerAR.style.display = "block";
          setTimeout(() => {
            containerAR.style.opacity = "1";
          }, 100);

          try {
            await deepAR.startCamera({
              facingMode: "environment",
              mirror: false,
            });
            cameraActivated();
          } catch (e) {
            alert(
              "Camera access failed. Please allow camera access and try again.\nError: " +
                e.message
            );
          }

          isARMode = true;
        } catch (error) {
          alert(
            "Failed to initialize AR. Please check your DeepAR license key and files."
          );
          hideLoader();
        }
      } else if (!toARMode && isARMode) {
        if (deepAR) {
          try {
            await deepAR.stopCamera();
          } catch (error) {}
        }

        containerAR.style.opacity = "0";
        setTimeout(() => {
          containerAR.style.display = "none";
        }, 500);

        canvas3D.style.display = "block";
        backgroundImg.style.display = "block";

        isARMode = false;

        if (currentModel !== currentARModel) {
          for (let i = 0; i < Object.keys(watchVariants).length; i++) {
            if (watchVariants[i].name === currentARModel) {
              setTimeout(() => loadWatchModel(i), 100);
              break;
            }
          }
        }
      }
    }

    // Load AR model
    async function loadARModel(variantIndex) {
      if (!deepAR || !isARMode) {
        return;
      }

      const variant = watchVariants[variantIndex];
      if (!variant) {
        return;
      }

      if (currentARModel === variant.name) {
        return;
      }

      try {
        await deepAR.switchEffect(variant.deepar);
        currentARModel = variant.name;
      } catch (error) {
        console.error("Error loading AR model:", error);
        throw error;
      }
    }

    // Smooth background transition function
    function changeBackgroundSmooth(newBackgroundSrc) {
      const goldBg = document.getElementById("background");
      const blackBg = document.getElementById("background-black");

      if (!goldBg || !blackBg) return;

      const isGoldBackground = newBackgroundSrc.includes("gold_bg");

      if (isGoldBackground) {
        goldBg.style.transition = "opacity 0.6s ease-in-out";
        blackBg.style.transition = "opacity 0.6s ease-in-out";
        goldBg.style.opacity = "1";
        blackBg.style.opacity = "0";
      } else {
        goldBg.style.transition = "opacity 0.6s ease-in-out";
        blackBg.style.transition = "opacity 0.6s ease-in-out";
        goldBg.style.opacity = "0";
        blackBg.style.opacity = "1";
      }
    }

    async function loadWatchModel(variantIndex) {
      if (isARMode) {
        const variant = watchVariants[variantIndex];
        if (!variant || currentARModel === variant.name) return;

        showLoader();

        try {
          await loadARModel(variantIndex);
          hideLoader();
        } catch (error) {
          hideLoader();
        }
      } else {
        const variant = watchVariants[variantIndex];
        if (!variant || currentModel === variant.name) return;

        showLoader();

        try {
          const manager = viewer.getManager();
          const importer = manager.importer;
          const canvas = document.getElementById("main-canvas");

          canvas.style.transition = "opacity 0.3s ease-in-out";
          canvas.style.opacity = "0";

          await new Promise((resolve) => setTimeout(resolve, 300));

          if (variant.background) {
            changeBackgroundSmooth(variant.background);
          }

          const rawAssets = await importer.importPath(variant.model, {
            processImported: false,
          });

          const processed = await importer.processImported(rawAssets, {
            autoScale: true,
            pseudoCenter: true,
          });

          viewer.scene.disposeSceneModels();
          viewer.renderer.refreshPipeline();
          await manager.addProcessedAssets(processed);

          canvas.style.opacity = "1";

          setTimeout(() => {
            animateArrows();
          }, 500);

          currentModel = variant.name;
          modelLoaded(currentModel);
          hideLoader();
        } catch (error) {
          console.error("Error loading model:", error);
          hideLoader();
        }
      }
    }

    async function setupViewer() {
      const canvas = document.getElementById("main-canvas");
      const poster = document.getElementById("poster");
      const spinnerContainer = document.querySelector(".dot-pulse-container");

      viewer = new ViewerApp({
        canvas: document.getElementById("main-canvas"),
      });

      await addBasePlugins(viewer);

      assetManager = viewer.getPlugin(AssetManagerPlugin);
      if (!assetManager) {
        assetManager = await viewer.addPlugin(AssetManagerPlugin);
      }

      await assetManager.addFromPath("/assets/models/IWC_Gold_3D.glb", {
        autoScale: true,
        pseudoCenter: true,
      });
      await viewer.setEnvironmentMap("/assets/hdr/neutral_3.hdr");

      const envTexture = viewer.scene.environment;

      if (envTexture) {
        envTexture.rotation = 0.0;
        envTexture.needsUpdate = true;
      }

      const controls = viewer.scene.activeCamera.controls;
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.rotateSpeed = 2.0;
      controls.minDistance = 7.0;
      controls.maxDistance = 10.0;
      controls.autoRotateSpeed = 1.0;
      controls.enablePan = false;
      controls.enableZoom = true;

      viewer.getPlugin(GroundPlugin).enabled = false;
      viewer.getPlugin(SSRPlugin).enabled = false;

      // Make background transparent - this is the key!
      const tonemapPlugin = viewer.getPlugin(TonemapPlugin);
      tonemapPlugin.config.clipBackground = true;
      tonemapPlugin.config.exposure = 1.2; // Set HDR intensity

      // Disable bloom effect
      //tonemapPlugin.viewer.renderer.passes.bloom = false;
      //   console.log(tonemapPlugin);

      const renderer = viewer.renderer.passes;
      if (renderer) {
        const bloomPass = renderer.find((pass) => pass.passId === "bloom");
        if (bloomPass) {
          bloomPass.enabled = false;
          // console.log("true");
          // bloomPass.passObject.power = 0.1;
          // bloomPass.passObject.radius = 0.2;
          // bloomPass.passObject.softThreshold = 0.2;
          // bloomPass.passObject.prefilter = {w: 0.06, x: 1.0, y: 0.3, z: 1.0};
          // console.log(bloomPass.passObject.power);
        }
      }

      // rendererUI.scale = window.devicePixelRatio;
      const ratio = window.devicePixelRatio;
      const rendererUI = await viewer.addPlugin(RendererUiPlugin);

      // Cap device pixel ratio for better performance
      if (ratio > 2.0) {
        rendererUI.scale = 2; // Reduced from 2.0 for better performance
      } else {
        rendererUI.scale = 1.5; // Standard for lower DPI devices
      }

      const interactionPrompt = viewer.getPlugin(InteractionPromptPlugin);
      interactionPrompt.enabled = false;

      const background = await viewer.addPlugin(SimpleBackgroundEnvUiPlugin);
      background.sceneBackground = null; // Make background transparent to show gold_bg.jpg

      setTimeout(() => {
        canvas.classList.add("visible");
        poster.style.display = "none"; // Hide poster when 3D model is ready
        hideLoader(); // Hide dot loader when 3D model is ready
        startHintTimer(); // Start hint cursor timer

        // Initialize swiper
        initSwiper();

        viewer.renderer.refreshPipeline();
        animateArrows();
        if (spinnerContainer) {
          spinnerContainer.style.display = "none";
        }
      }, 750);

      setTimeout(() => {
        // const interval = setInterval(function() {
        // 	animateRotationHint();
        // }, 10000);
      }, 3000);

      setTimeout(() => {
        controls.autoRotate = true;
      }, 4000);

      // Add screenshot functionality
      setupScreenshotButton();

      // Add performance optimization - pause animations when page not visible
      setupVisibilityOptimization();

      // Add interaction listeners to stop hint
      const handleInteractionStart = () => {
        userTappedOnce = true;
        interactionWithModel(currentModel);
        
        if (hint) hint.style.display = "none";
        if (hintTimer) {
          clearInterval(hintTimer);
          hintTimer = null;
        }
      };

      canvas.addEventListener("pointerdown", handleInteractionStart);
      // canvas.addEventListener("touchstart", handleInteractionStart);

      return;
    }

    function initSwiper() {
      const swiperContainer = document.getElementById("swiper-container");
      if (swiperContainer) {
        swiperContainer.style.display = "block";
      }

      const swiper = initWatchVariantsSwiper((activeIndex) => {
        loadWatchModel(activeIndex);
      });

      return swiper;
    }

    function animateRotationHint() {
      if (userTappedOnce) return;
      if (!hint) return;

      hint.style.display = "block";
      hint.classList.add("animateRight");
      hint.addEventListener("animationend", (event) => {
        if (event.animationName === "moveRight1") {
          hint.classList.add("animateLeft");
        } else if (event.animationName === "moveLeft") {
          hint.classList.add("animateToInitial");
        } else if (event.animationName === "moveRight2") {
          hint.style.display = "none";
          hint.classList.remove(
            "animateRight",
            "animateLeft",
            "animateToInitial"
          );
        }
      });
    }

    function startHintTimer() {
      if (hintTimer) {
        clearInterval(hintTimer);
      }
      hintTimer = setInterval(() => {
        if (!userTappedOnce && !isARMode) {
          animateRotationHint();
        }
      }, HINT_DELAY + 2000);
    }

    function setupScreenshotButton() {
      document
        .getElementById("screenshot-btn")
        .addEventListener("click", () => {
          // Stop auto rotation temporarily for a clean screenshot
          const controls = viewer.scene.activeCamera.controls;
          const wasAutoRotating = controls.autoRotate;
          controls.autoRotate = false;

          setTimeout(() => {
            // Get the canvas from WebGI viewer
            const canvas = document.getElementById("main-canvas");

            // Force a render to ensure everything is up to date
            viewer.renderer.refreshPipeline();

            setTimeout(() => {
              // Capture the canvas as image
              canvas.toBlob((blob) => {
                if (blob) {
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "hublot-watch-poster.png";
                  a.click();
                  URL.revokeObjectURL(url);
                }
              }, "image/png");

              // Restore auto rotation if it was enabled
              if (wasAutoRotating) {
                controls.autoRotate = true;
              }
            }, 100);
          }, 100);
        });
    }

    function setupVisibilityOptimization() {
      document.addEventListener("visibilitychange", () => {
        if (!viewer) return;

        const controls = viewer.scene.activeCamera.controls;

        if (document.hidden) {
          controls.autoRotate = false;
          if (
            viewer.renderer &&
            typeof viewer.renderer.setFrameRate === "function"
          ) {
            viewer.renderer.setFrameRate(10);
          }
        } else {
          if (!isARMode) {
            setTimeout(() => {
              controls.autoRotate = true;
            }, 1000);
          }
          if (
            viewer.renderer &&
            typeof viewer.renderer.setFrameRate === "function"
          ) {
            viewer.renderer.setFrameRate(60);
          }
        }
      });

      window.addEventListener("blur", () => {
        if (
          viewer &&
          viewer.renderer &&
          typeof viewer.renderer.setFrameRate === "function"
        ) {
          viewer.renderer.setFrameRate(15);
        }
      });

      window.addEventListener("focus", () => {
        if (
          viewer &&
          viewer.renderer &&
          typeof viewer.renderer.setFrameRate === "function"
        ) {
          viewer.renderer.setFrameRate(60);
        }
      });
    }

    async function animateArrows() {
      const popmotion = await viewer.addPlugin(PopmotionPlugin);
      const now = new Date();

      // Find the arrow groups by their names (capitalized!)
      const secondsGroup = viewer.scene.findObjectsByName("Seconds")[0];
      const minutesGroup = viewer.scene.findObjectsByName("Minutes")[0];
      const hoursGroup = viewer.scene.findObjectsByName("Hours")[0];

      if (secondsGroup) {
        // Get the built-in offset of the seconds hand in the 3D model
        const modelOffset = secondsGroup.rotation.z;

        // Calculate target position based on current time, accounting for model offset
        const seconds = now.getSeconds();
        const targetAngle = (-Math.PI * 2 * seconds) / 60 + modelOffset;

        // Start from 12:00 position (adding model offset to compensate)
        const startAngle = modelOffset;

        // Always start animation from 12:00 position to current time
        popmotion.animate({
          from: startAngle,
          to: targetAngle,
          duration: 2000,
          ease: EasingFunctions.easeInOut,
          onUpdate: (v) => {
            secondsGroup.rotation.set(0, 0, v);
            viewer.setDirty();
          },
          onComplete: () => {
            // Continue with real-time animation
            popmotion.animate({
              from: targetAngle,
              to: targetAngle - 2 * Math.PI,
              duration: 60 * 1000,
              repeat: 10000,
              ease: EasingFunctions.linear,
              onUpdate: (v) => {
                secondsGroup.rotation.set(0, 0, v);
                viewer.setDirty();
              },
            });
          },
        });
      }

      if (minutesGroup) {
        // minutes - rotate all children in the Minutes group
        const minutes = now.getMinutes() + now.getSeconds() / 60;
        const minutesAngle = (Math.PI * 2 * minutes) / 60;

        popmotion.animate({
          from: 0,
          to: -minutesAngle,
          duration: 2000,
          ease: EasingFunctions.easeInOut,
          onUpdate: (v) => {
            // Rotate all children in the Minutes group
            minutesGroup.children.forEach((child) => {
              if (child.rotation) {
                child.rotation.set(0, 0, v);
              }
            });
            viewer.setDirty();
          },
          onComplete: () => {
            popmotion.animate({
              from: -minutesAngle,
              to: -minutesAngle - 2 * Math.PI,
              duration: 60 * 60 * 1000,
              repeat: 10000,
              ease: EasingFunctions.linear,
              onUpdate: (v) => {
                minutesGroup.children.forEach((child) => {
                  if (child.rotation) {
                    child.rotation.set(0, 0, v);
                  }
                });
                viewer.setDirty();
              },
            });
          },
        });
      }

      if (hoursGroup) {
        // hours - rotate all children in the Hours group
        const hours = (now.getHours() % 12) + now.getMinutes() / 60;
        const hoursAngle = (Math.PI * 2 * hours) / 12;

        popmotion.animate({
          from: 0,
          to: -hoursAngle,
          duration: 2000,
          ease: EasingFunctions.easeInOut,
          onUpdate: (v) => {
            // Rotate all children in the Hours group
            hoursGroup.children.forEach((child) => {
              if (child.rotation) {
                child.rotation.set(0, 0, v);
              }
            });
            viewer.setDirty();
          },
          onComplete: () => {
            popmotion.animate({
              from: -hoursAngle,
              to: -hoursAngle - 2 * Math.PI,
              duration: 60 * 60 * 12 * 1000,
              repeat: 10000,
              ease: EasingFunctions.linear,
              onUpdate: (v) => {
                hoursGroup.children.forEach((child) => {
                  if (child.rotation) {
                    child.rotation.set(0, 0, v);
                  }
                });
                viewer.setDirty();
              },
            });
          },
        });
      }
    }
  